// Chat select mode (mobile: src/components/message/v2/pro/proSelection.js).
// One selection holds message ids; a merged photo album contributes the ids of
// all its images (as forwarding does). These pure helpers let the header offer
// copy · forward · delete under the mobile rules.

import moment from "moment";
import { decryptMessage } from "./encryptDecryptFunction";

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/** Every rendered message of the conversation sections, oldest first. */
export function flattenSections(sections) {
  const out = [];
  (Array.isArray(sections) ? sections : []).forEach((section) => {
    (section?.items || []).forEach((item) => {
      if (item) out.push(item);
    });
  });
  return out;
}

/** System rows (joined, left, deleted profile, …) never take part in a selection. */
export function isSystemRow(chat) {
  return !!(
    chat &&
    (chat.joinedGroup ||
      chat.leftGroup ||
      chat.pendingGroup ||
      chat.rejectedGroup ||
      chat.userDeleted ||
      chat.userDeactivated ||
      chat.userReactivate)
  );
}

/** Can this row be ticked in select mode? */
export function isSelectableRow(chat) {
  if (!chat || chat.isDeleted || isSystemRow(chat)) return false;
  if (chat.message === "You have blocked this contact") return false;
  return true;
}

/** The ids a row stands for: the message, plus every image of a merged album. */
export function selectionIdsOf(chat) {
  if (!chat) return [];
  const ids = [];
  const push = (id) => {
    if (id != null && !ids.some((x) => sameId(x, id))) ids.push(id);
  };
  push(chat.id);
  if (Array.isArray(chat.images) && chat.images.length > 1) chat.images.forEach((img) => push(img?.id));
  return ids;
}

/** Is this row (or any image of its album) in the selection? */
export function isRowSelected(chat, msgIds) {
  const ids = Array.isArray(msgIds) ? msgIds : [];
  return selectionIdsOf(chat).some((id) => ids.some((sel) => sameId(sel, id)));
}

/** Adds or removes a row (with its album images) from the selection. */
export function toggleRowSelection(chat, msgIds) {
  const ids = Array.isArray(msgIds) ? [...msgIds] : [];
  const rowIds = selectionIdsOf(chat);
  if (isRowSelected(chat, ids)) return ids.filter((sel) => !rowIds.some((id) => sameId(id, sel)));
  rowIds.forEach((id) => {
    if (!ids.some((sel) => sameId(sel, id))) ids.push(id);
  });
  return ids;
}

/** The message behind a selection entry (a message id, or an image id of an album). */
export function findSelectedMessage(list, sel) {
  const arr = Array.isArray(list) ? list : [];
  return (
    arr.find((m) => m && (sameId(m.id, sel) || sameId(m.messageRequestId, sel))) ||
    arr.find((m) => m && Array.isArray(m.images) && m.images.some((img) => img && sameId(img.id, sel))) ||
    null
  );
}

/** The distinct messages of a selection, oldest first. */
export function selectedMessages(list, msgIds) {
  const seen = new Set();
  const out = [];
  (Array.isArray(msgIds) ? msgIds : []).forEach((sel) => {
    const m = findSelectedMessage(list, sel);
    const key = m ? String(m.id || m.messageRequestId || "") : "";
    if (!m || seen.has(key)) return;
    seen.add(key);
    out.push(m);
  });
  return out.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
}

/**
 * Delete is offered only when every selected message is mine and still
 * deletable: not deleted, not a poll (polls delete from their menu), sent at
 * most 10 minutes ago.
 */
export function allSelectedDeletable(list, msgIds, myId, now = moment()) {
  const ids = Array.isArray(msgIds) ? msgIds : [];
  if (!ids.length || myId == null) return false;
  return ids.every((sel) => {
    const m = findSelectedMessage(list, sel);
    return (
      !!m &&
      sameId(m.senderId, myId) &&
      !m.isDeleted &&
      m.messageType !== "poll" &&
      ((m.local && !m.isSent) || now.diff(m.createdAt, "minutes") <= 10)
    );
  });
}

/** Ids to delete for a selection (an album deletes every one of its images). */
export function deleteIdsOf(list, msgIds) {
  const out = [];
  const push = (id) => {
    if (id != null && !out.some((x) => sameId(x, id))) out.push(id);
  };
  selectedMessages(list, msgIds).forEach((m) => selectionIdsOf(m).forEach(push));
  return out;
}

const stripHtml = (text) =>
  String(text || "")
    .replace(/<br\s*[/]?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

/**
 * Plain text of one message, as the menu's Copy builds it: the transcript of a
 * voice message, the original of a received translated message, decrypted,
 * without HTML.
 */
export function copyTextOf(m, { myId, secretKey } = {}) {
  if (!m || m.isDeleted) return "";
  if (m.messageType === "audio") return stripHtml(m.originalTranscribedAudio || m.transcribedAudio || "");
  const incoming = !sameId(m.senderId, myId);
  let raw = (incoming && m.originalMessage) || m.message || "";
  if (typeof raw === "string" && raw.startsWith("U2FsdGVkX1") && secretKey) {
    try {
      raw = decryptMessage(raw, secretKey) || "";
    } catch (_) {
      raw = "";
    }
  }
  if (typeof raw === "string" && raw.startsWith("U2FsdGVkX1")) return "";
  return stripHtml(raw);
}

/** Text of a whole selection, oldest first, a blank line between messages. */
export function selectionCopy(list, msgIds, opts) {
  const parts = selectedMessages(list, msgIds)
    .map((m) => copyTextOf(m, opts))
    .filter(Boolean);
  return { text: parts.join("\n\n"), count: parts.length };
}
